<script setup lang="ts">
/**
 * One field of the schedule editor's form: its label, then its control.
 *
 * In a bulk edit every field starts as "Keep as is", so the rows keep what they have. "Change"
 * beside the label shows the control, and "Keep as is" hides it again. For one schedule the
 * field is just its label and its control.
 */
withDefaults(
    defineProps<{
        label: string;
        /** The control's id, so the label focuses it. Left out, the label is plain text. */
        forId?: string;
        bulk?: boolean;
        /** The field's name, for test ids. */
        name: string;
    }>(),
    { forId: undefined, bulk: false },
);

const changed = defineModel<boolean>('changed', { default: false });
</script>

<template>
    <div class="flex flex-col gap-1.5" :data-testid="bulk ? `schedule-bulk-field-${name}` : undefined" :data-changed="bulk ? (changed ? 'true' : 'false') : undefined">
        <div class="flex min-w-0 items-center justify-between gap-2">
            <label v-if="forId && (!bulk || changed)" :for="forId" class="text-sm font-medium"><slot name="label">{{ label }}</slot></label>
            <span v-else class="text-sm font-medium"><slot name="label">{{ label }}</slot></span>
            <button
                v-if="bulk"
                type="button"
                class="text-muted-foreground hover:text-foreground shrink-0 text-xs underline-offset-2 hover:underline"
                :aria-label="changed ? `Keep ${label} as is` : `Change ${label}`"
                :data-testid="`schedule-bulk-toggle-${name}`"
                @click="changed = !changed"
            >
                {{ changed ? 'Keep as is' : 'Change' }}
            </button>
        </div>
        <slot v-if="!bulk || changed" />
        <p v-else class="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-sm" :data-testid="`schedule-bulk-keep-${name}`">Keep as is</p>
    </div>
</template>
